import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { ProspectCall, CallOutcome } from '@/types/prospection'
import toast from 'react-hot-toast'
import { emailService } from '@/services/email'
import { createProspectAppointmentConfirmationEmail } from '@/utils/emailTemplates'
import { agendaService } from '@/services/agenda'

export function useProspectCalls(prospectId: string | undefined) {
  return useQuery({
    queryKey: ['prospect-calls', prospectId],
    queryFn: async () => {
      if (!prospectId) return []
      const { data, error } = await (supabase
        .from('prospect_calls') as any)
        .select('*')
        .eq('prospect_id', prospectId)
        .order('call_date', { ascending: false })

      if (error) throw error
      return (data || []) as ProspectCall[]
    },
    enabled: !!prospectId,
  })
}

export function useUpcomingCallbacks() {
  return useQuery({
    queryKey: ['prospect-calls', 'upcoming'],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('prospect_calls') as any)
        .select('*, prospects!inner(id, nom_commercial, raison_sociale, telephone, email, statut)')
        .not('next_appointment', 'is', null)
        .gte('next_appointment', new Date().toISOString())
        .order('next_appointment', { ascending: true })
        .limit(20)

      if (error) throw error
      return (data || []) as (ProspectCall & { prospects: any })[]
    },
  })
}

interface CreateCallPayload {
  prospect_id: string
  call_date?: string
  duration_minutes?: number | null
  call_script?: string | null
  outcome?: CallOutcome | null
  next_appointment?: string | null
  notes?: string | null
  contact_email?: string | null
}

const ESPACE_CLIENT_URL = 'https://client.lessablesweb.fr'

/**
 * Calcule l'instant d'envoi du rappel : 9h00 (Europe/Paris) la veille du RDV.
 * Retourne null si ce créneau est déjà passé (RDV à moins de ~24h).
 */
function computeReminderScheduledAt(nextAppointment: string): string | null {
  const rdv = new Date(nextAppointment)

  // Date de la veille, au format YYYY-MM-DD dans le fuseau Europe/Paris.
  const dayBefore = new Date(rdv.getTime() - 24 * 60 * 60 * 1000)
  const parisDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dayBefore)

  // 9h00 Europe/Paris ce jour-là. L'offset de Paris est +01:00 (hiver) ou
  // +02:00 (été) ; on le déduit en comparant l'heure locale Paris à l'UTC.
  const probe = new Date(`${parisDate}T09:00:00Z`)
  const parisHour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      hour: '2-digit',
      hour12: false,
    }).format(probe)
  )
  // parisHour = heure locale Paris correspondant à 09:00 UTC => offset = parisHour - 9
  const offsetHours = parisHour - 9
  const scheduledAt = new Date(`${parisDate}T09:00:00Z`)
  scheduledAt.setUTCHours(scheduledAt.getUTCHours() - offsetHours)

  if (scheduledAt.getTime() <= Date.now()) return null
  return scheduledAt.toISOString()
}

export function useCreateCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateCallPayload) => {
      const { data: userData } = await supabase.auth.getUser()

      const { data, error } = await (supabase
        .from('prospect_calls') as any)
        .insert({
          ...payload,
          call_date: payload.call_date || new Date().toISOString(),
          created_by: userData.user?.id || null,
        })
        .select()
        .single()

      if (error) throw error

      // Update prospect next_appointment if set
      if (payload.next_appointment) {
        await (supabase
          .from('prospects') as any)
          .update({ next_appointment: payload.next_appointment })
          .eq('id', payload.prospect_id)
      }

      // Workflow confirmation + rappel J-1 : uniquement si un RDV est planifié
      // ET qu'on dispose d'une adresse email. C'est CallPanel qui garantit la
      // présence d'un email valide quand un RDV est saisi (bouton désactivé
      // sinon) ; ce hook ne fait que refléter cette invariante côté UI.
      if (payload.next_appointment && payload.contact_email) {
        const email = payload.contact_email.trim()

        // 1. Mettre à jour l'email de la fiche prospect.
        await (supabase
          .from('prospects') as any)
          .update({ email })
          .eq('id', payload.prospect_id)

        // 2. Récupérer le nom du contact pour personnaliser l'email.
        const { data: prospectRow } = await (supabase
          .from('prospects') as any)
          .select('contact_name, dirigeant, nom_commercial, raison_sociale')
          .eq('id', payload.prospect_id)
          .single()

        const contactName =
          prospectRow?.contact_name ||
          prospectRow?.dirigeant ||
          prospectRow?.nom_commercial ||
          prospectRow?.raison_sociale ||
          'Madame, Monsieur'

        // 3. Envoyer la confirmation immédiatement (non bloquant en cas d'échec).
        try {
          const html = createProspectAppointmentConfirmationEmail({
            contactName,
            dateHeure: payload.next_appointment,
            espaceClientUrl: ESPACE_CLIENT_URL,
          })
          const sendResult = await emailService.sendEmail({
            to: email,
            toName: contactName,
            subject: 'Confirmation de votre rendez-vous - Les Sables Web',
            html,
            text: `Bonjour ${contactName},\n\nNous vous confirmons votre rendez-vous du ${new Date(
              payload.next_appointment
            ).toLocaleString('fr-FR')}.\n\nCréez votre compte sur notre plateforme : ${ESPACE_CLIENT_URL}\n\nCordialement,\nL'équipe Les Sables Web`,
            referenceType: 'prospect',
            referenceId: payload.prospect_id,
          })
          if (!sendResult.success) {
            toast.error(`Confirmation non envoyée : ${sendResult.error}`)
          }
        } catch {
          toast.error('Confirmation de RDV non envoyée (erreur email)')
        }

        // 4. Planifier le rappel J-1 (9h la veille). Non bloquant.
        try {
          const scheduledAt = computeReminderScheduledAt(payload.next_appointment)
          if (scheduledAt) {
            const { error: reminderError } = await (supabase
              .from('scheduled_prospect_reminders') as any)
              .insert({
                call_id: data.id,
                prospect_id: payload.prospect_id,
                scheduled_at: scheduledAt,
                created_by: userData.user?.id || null,
              })
            if (reminderError) {
              console.error('scheduled_prospect_reminders insert', reminderError)
              toast.error('Rappel J-1 non planifié')
            }
          }
        } catch (err) {
          console.error('computeReminderScheduledAt / insert reminder', err)
          toast.error('Rappel J-1 non planifié')
        }

        // 5. Créer un événement dans l'agenda (RDV confirmé) et lier l'appel au RDV.
        try {
          const client = await agendaService.getOrCreateClient(email, contactName)
          const prospectLabel =
            prospectRow?.nom_commercial ||
            prospectRow?.raison_sociale ||
            'prospect'
          const rdv = await agendaService.createRendezVous({
            client_id: client.id,
            professionnel_id: userData.user?.id || undefined,
            date_heure: payload.next_appointment,
            description: `Prospection — ${prospectLabel}`,
            statut: 'confirme',
          })
          await (supabase
            .from('prospect_calls') as any)
            .update({ rendez_vous_id: rdv.id })
            .eq('id', data.id)
        } catch (err) {
          console.error('createRendezVous (prospection)', err)
          toast.error("RDV non ajouté à l'agenda")
        }
      }

      // Auto-update prospect status to "contacte" if still "lead"
      if (payload.outcome && payload.outcome !== 'no_answer' && payload.outcome !== 'voicemail') {
        const { data: prospect } = await (supabase
          .from('prospects') as any)
          .select('statut')
          .eq('id', payload.prospect_id)
          .single()

        if (prospect?.statut === 'lead') {
          await (supabase
            .from('prospects') as any)
            .update({ statut: 'contacte' })
            .eq('id', payload.prospect_id)
        }
      }

      return data as ProspectCall
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['prospect-calls', variables.prospect_id] })
      queryClient.invalidateQueries({ queryKey: ['prospect-calls', 'upcoming'] })
      queryClient.invalidateQueries({ queryKey: ['prospects'] })
      toast.success('Appel enregistre')
    },
    onError: () => {
      toast.error('Erreur lors de l\'enregistrement de l\'appel')
    },
  })
}

export function useDeleteCall() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, prospectId }: { id: string; prospectId: string }) => {
      // Récupérer le RDV agenda lié avant la suppression (FK ON DELETE SET NULL).
      const { data: callRow } = await (supabase
        .from('prospect_calls') as any)
        .select('rendez_vous_id')
        .eq('id', id)
        .single()

      const { error } = await (supabase
        .from('prospect_calls') as any)
        .delete()
        .eq('id', id)

      if (error) throw error

      // Annuler le RDV agenda associé (non bloquant).
      if (callRow?.rendez_vous_id) {
        try {
          await agendaService.annulerRendezVous(callRow.rendez_vous_id)
        } catch (err) {
          console.error('annulerRendezVous on call delete', err)
          toast.error("RDV agenda non annulé (à vérifier manuellement)")
        }
      }

      return prospectId
    },
    onSuccess: (prospectId) => {
      queryClient.invalidateQueries({ queryKey: ['prospect-calls', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['prospect-calls', 'upcoming'] })
      toast.success('Appel supprime')
    },
    onError: () => {
      toast.error('Erreur lors de la suppression')
    },
  })
}
